/* eslint-disable no-unused-vars */
import React, { useRef, useCallback, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";

import { cloneDeep } from "../../../../utils/cloneDeep";
import { documentValidationSchema } from "../../../../utils/validation";
import { UPLOADED } from "../../../../constants";
import { docUpload, addMultiDocument } from "../../../../store/actions/uploadDocuments";
import { DocumentRow } from "../../DocumentRow";
import { log } from "../../../../utils/loggger";
import { getDocumentFileName } from "../../../../utils/getDocumentFileName";
import { getProspectId } from "../../../../store/selectors/appConfig";
import { PRE_ELIGIBILITY_DOC } from "../../constants";

import {
  MultiDocumentContainer,
  MultiDocumentContent,
  MultiDocumentSidebar,
  MultiDocumentRow as MultiDocRow,
  DownloadStepsContainer,
  DefaultInput,
  ControlsBox,
  DownloadLabel,
  AddIcon
} from "../styled";

/**
 * Component for rendering multiple document upload rows
 *
 * @component
 */
export const MultiDocumentRow = ({
  document,
  type: docOwner,
  stakeholderIndex,
  infoMessage = "",
  displayValidationMsg = false,
  downloadUrl = "",
  downloadLabel = ""
}) => {
  const inputEl = useRef(null);
  const dispatch = useDispatch();
  const [multiDoc, setMultiDoc] = useState(false);
  const [addMoreDisable, setAddMoreDisable] = useState(false);
  const [multiSelectedFile, setMultiSelectedFile] = useState(null);
  const prospectId = useSelector(getProspectId);
  const {
    groupTitle: documentTitle,
    documentType,
    prospectDocument: documents,
    limit = 1,
    uniqueId,
    required
  } = document;

  useEffect(() => {
    if (limit > 1 && (documents.length > 1 || documents[0].uploadStatus === "Uploaded")) {
      setMultiDoc(true);
    } else {
      setMultiDoc(false);
    }
    const NotUploaded = documents.some(document => document.uploadStatus === "NotUploaded");
    const addMoreStatus = NotUploaded || (documents.length >= limit ? true : false);
    setAddMoreDisable(addMoreStatus);
  }, [documents, limit]);

  const index = documents.length + 1;
  const uploadMultiDocument = useCallback(
    (file, document = "") => {
      let newDocument = {};
      let documentIndex = 0;
      if (stakeholderIndex) {
        documentIndex = parseInt(stakeholderIndex.charAt(0));
      }
      if (document) {
        newDocument = document ? cloneDeep(document) : [];
      } else {
        newDocument = documents[0] ? cloneDeep(documents[0]) : [];
        newDocument.documentKey = documentType + "-" + documents.length;
        newDocument.uploadStatus = "NotUploaded";
        newDocument.fileName = null;
        newDocument.fileSize = file.size;
        newDocument.fileFormat = file.type;
        newDocument.submittedBy = null;
        newDocument.submittedDt = null;
        const docProps = {
          stakeholderIndex,
          docOwner,
          document: newDocument
        };
        dispatch(addMultiDocument(docProps));
      }
      setMultiSelectedFile(file);
      const { documentKey } = newDocument;

      const docProps = {
        uploadStatus: UPLOADED,
        fileSize: file.size,
        submittedDt: file.lastModifiedDate,
        fileFormat: file.type
      };
      const data = new FormData();
      data.append("documentType", documentType);
      data.append("documentIndex", documentIndex);
      data.append("documentKey", documentKey);
      data.append("uploadfile", file);
      data.append("fileFormat", file.type);
      data.append("fileName", getDocumentFileName(documentType, prospectId, file.name));

      dispatch(
        docUpload({
          data,
          docProps,
          docOwner,
          documentType,
          documentKey,
          uniqueId,
          index,
          userFileName: file.name,
          stakeholderIndex,
          documentTitle
        })
      );
    },
    [
      docOwner,
      dispatch,
      index,
      stakeholderIndex,
      documents,
      documentType,
      documentTitle,
      prospectId,
      uniqueId
    ]
  );

  const fileUploadClick = event => (event.target.value = null);
  const fileUploadChange = useCallback(() => {
    const file = inputEl.current.files[0];
    try {
      documentValidationSchema.validateSync({ file }, { abortEarly: false });
      uploadMultiDocument(file);
    } catch (error) {
      log(error.message);
    }
  }, [uploadMultiDocument]);
  return (
    <MultiDocumentContainer>
      {downloadUrl && (
        <DownloadStepsContainer>
          <DownloadLabel component="h5"> Step 1 </DownloadLabel>
          <DownloadLabel component="h5">
            Download and complete the{" "}
            <a
              style={{ textDecoration: "underline" }}
              href={downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => {
                e.stopPropagation();
              }}
            >
              {downloadLabel}
            </a>{" "}
            template
          </DownloadLabel>
          <DownloadLabel component="h5"> Step 2 </DownloadLabel>
        </DownloadStepsContainer>
      )}
      <MultiDocRow>
        <MultiDocumentContent multiDoc={multiDoc}>
          {documents.map((document, index) => {
            return (
              <DocumentRow
                key={`${document.documentType}-${document.documentKey}`}
                index={index}
                document={document}
                type={docOwner}
                infoMessage={!multiDoc ? infoMessage : null}
                stakeholderIndex={stakeholderIndex}
                docRemoveWarning={multiDoc}
                multiDoc={true}
                uploadMultiDocument={(file, document) => uploadMultiDocument(file, document)}
                multiSelectedFile={multiSelectedFile}
                required={required}
                displayValidationMsg={displayValidationMsg}
                uniqueId={uniqueId}
                parentGroupTitle={documentTitle}
              />
            );
          })}
        </MultiDocumentContent>
        {multiDoc && (
          <MultiDocumentSidebar>
            <DefaultInput
              name="file"
              type="file"
              onChange={fileUploadChange}
              onClick={fileUploadClick}
              ref={inputEl}
              disabled={addMoreDisable}
            />
            <ControlsBox
              component="p"
              sx={{
                ...(addMoreDisable && {
                  color: "#ffffff",
                  boxShadow: "none",
                  cursor: "default",
                  backgroundColor: "#c6c6cc",
                  border: "none"
                })
              }}
              onClick={() => !addMoreDisable && inputEl.current.click()}
            >
              <AddIcon alt="add" /> Add More
            </ControlsBox>
          </MultiDocumentSidebar>
        )}
      </MultiDocRow>
    </MultiDocumentContainer>
  );
};

/**
 * PropTypes for MultiDocumentRow component
 */
MultiDocumentRow.propTypes = {
  /**
   * Document object containing metadata and file details
   */
  document: PropTypes.shape({
    /**
     * Title of the document group
     */
    groupTitle: PropTypes.string,

    /**
     * Type of document
     */
    documentType: PropTypes.string.isRequired,

    /**
     * Array of prospect documents
     */
    prospectDocument: PropTypes.arrayOf(
      PropTypes.shape({
        /**
         * Document unique key
         */
        documentKey: PropTypes.string,

        /**
         * Upload status of the document
         */
        uploadStatus: PropTypes.string,

        /**
         * File name of the uploaded document
         */
        fileName: PropTypes.string,

        /**
         * Size of the uploaded file
         */
        fileSize: PropTypes.number,

        /**
         * Format/type of the uploaded file
         */
        fileFormat: PropTypes.string
      })
    ).isRequired,

    /**
     * Maximum number of documents allowed
     */
    limit: PropTypes.number,

    /**
     * Unique identifier for the document
     */
    uniqueId: PropTypes.string,

    /**
     * Whether the document is required
     */
    required: PropTypes.bool
  }).isRequired,

  /**
   * Type of document owner (company or stakeholder)
   */
  type: PropTypes.string.isRequired,

  /**
   * Index of the stakeholder if applicable
   */
  stakeholderIndex: PropTypes.string,

  /**
   * Additional information message for the document
   */
  infoMessage: PropTypes.string,

  /**
   * Whether to display validation messages
   */
  displayValidationMsg: PropTypes.bool,

  /**
   * URL for downloadable template if applicable
   */
  downloadUrl: PropTypes.string,

  /**
   * Label for the download link if applicable
   */
  downloadLabel: PropTypes.string
};
